import {
  CharacterType,
  ICharacter,
  useApp,
  useAppSelector,
  useTranslation,
  useUserInfo,
  useUserInteractions,
  useUserSettings,
} from "@next-platform/client-common-core";
import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import { Spacer } from "@nextui-org/react";
import type { UiState } from "instantsearch.js";
import { history } from "instantsearch.js/es/lib/routers";
import React, { useMemo, useState } from "react";
import { Configure, ConfigureProps, InstantSearch, Stats } from "react-instantsearch";
import { useNavigate } from "react-router-dom";

import BannedBanner from "@/components/BannedBanner";
import { Col, Row, Spinner } from "@/components/customComponents";
import FilterBox from "@/components/search/FilterBox/FilterBox";
import HeroBanner from "@/components/search/HeroBanner";
import useShouldFilterNsfwCharacters from "@/components/search/hooks/useShouldFilterNsfwCharacters";
import NsfwToggle from "@/components/search/NsfwToggle";
import SearchClientPagination from "@/components/search/SearchClientPagination";
import SearchTagFilterTrigger from "@/components/search/SearchTagFilterTrigger";
import { parseInstantSearchUrlState } from "@/components/search/utils/parseInstantSearchUrlState";
import SharedChatbotsSearchBar from "@/components/search/v2/SharedChatbotsSearchBar";
import SharedChatbotsSortDropdown from "@/components/search/v2/SharedChatbotsSortDropdown";
import WaitingQueueHeader from "@/components/search/WaitingQueuHeader";
import { NSFW_TAGS, TAGS_FILTER_LOCAL_STORAGE_KEY, TAGS_TO_EXCLUDE, TYPESENSE_TEXT_MATCH_SORT_PREFIX } from "@/constants";
import { useSharedChatbotsSearch } from "@/hooks";
import { Collection, TypesenseApiKey, TypesenseCollectionName } from "@/types/api";
import { typedLocalStorageService } from "@/utils/localStorageRe";

import { CharacterItemAction, DataSourceType, DisplayContext, HeaderComponentType, SearchListingConfig } from "../../types";
import CharacterHitsV2 from "../HitsV2";
import { createGroupAddableMemberFilter } from "./utils/createGroupAddableMemberFilter";

interface IStateChangeParams {
  uiState: UiState;
  setUiState: (state: UiState) => void;
}

interface ISharedChatbotsListingProps {
  // Data for filters
  charactersList?: string[];
  username?: string;
  blockedUsers?: string[];

  // Shared state
  nsfwToggle: boolean;
  setNsfwToggle: (value: boolean) => void;
  showNSFW: boolean;
  isPartner: boolean;

  // Config
  config?: SearchListingConfig;
  recommId: string;
  useStoreTagFilters: boolean;

  // Auth and permissions
  isAuthenticated: boolean;
  permissions?: string[];
  role?: string;

  // Custom filters
  customFilters?: React.ReactNode;

  // Data source type
  dataSourceType?: DataSourceType;

  // Selection props (from MEMBER_SELECT context)
  onItemAction?: (action: CharacterItemAction, actionItem: ICharacter | undefined) => void;
  selectedItems?: string[];
  maxItemsToSelect?: number;
  onLimitReached?: (currentCount: number, maxCount: number) => void;
  collection?: Collection;
}

const SharedChatbotsListing = ({
  charactersList = [],
  username,
  blockedUsers,
  nsfwToggle,
  setNsfwToggle,
  showNSFW,
  isPartner,
  config,
  recommId,
  useStoreTagFilters,
  isAuthenticated,
  permissions,
  customFilters,
  dataSourceType,
  onItemAction,
  selectedItems,
  maxItemsToSelect,
  onLimitReached,
  collection = Collection.CHARACTERS,
}: ISharedChatbotsListingProps) => {
  const navigate = useNavigate();

  // Get hooks and state
  const { typesenseConfig: searchClientRemoteConfig } = useApp();
  const applicationId = basicAppConfig.applicationId;
  const { onlyUserId } = useUserInfo();
  const { setUserSortBy } = useUserInteractions();
  const { areUserSettingsLoaded } = useUserSettings();
  const { onboardingFinished, showOnboardingModal } = useAppSelector((state) => state.navigation) || {};
  const { isEffectiveNsfwToggleEnabled, isNsfwCharacterDiscoveryAllowed, isNsfwToggleAllowed, shouldFilterNsfwCharacters } =
    useShouldFilterNsfwCharacters({
      isNsfwToggled: nsfwToggle,
      isShowNsfwEnabled: showNSFW,
    });

  // Local state
  const [stateIsTagsDrawerVisible, setStateIsTagsDrawerVisible] = useState(false);
  const tagListMinimizedLocalStorage = typedLocalStorageService.getItem(TAGS_FILTER_LOCAL_STORAGE_KEY);
  const [stateIsTagListMinimized, setStateIsTagListMinimized] = useState(tagListMinimizedLocalStorage === "true");
  const [stateIsFoundNegativeKeyword, setStateIsFoundNegativeKeyword] = useState(false);
  const [stateCurrentCategory, setStateCurrentCategory] = useState("");

  // Typesense setup
  const shouldDelayTypesenseQuery = isAuthenticated && !areUserSettingsLoaded && !onboardingFinished && !showOnboardingModal;
  const {
    instantSearchAdapter,
    queryBy,
    isReady: isSharedChatbotsReady,
  } = useSharedChatbotsSearch({
    shouldDelayQuery: shouldDelayTypesenseQuery,
    collection,
  });

  const { t } = useTranslation();
  const statsTranslations = useMemo(
    () => ({ rootElementText: ({ nbHits }: { nbHits: number }) => t("common:results.found", { count: nbHits }) }),
    [t],
  );

  const typesenseApiKey = searchClientRemoteConfig?.[TypesenseApiKey[collection]];
  // Loading check
  if (!applicationId || !typesenseApiKey || !isSharedChatbotsReady) {
    return <Spinner centerToScreen={true} />;
  }

  const searchClient = instantSearchAdapter?.searchClient;

  // Build index names
  const baseIndexName = searchClientRemoteConfig?.[TypesenseCollectionName[collection]];
  let searchIndexName = `${baseIndexName}/sort/${TYPESENSE_TEXT_MATCH_SORT_PREFIX}`;
  if (collection !== Collection.LOREBOOKS) {
    searchIndexName += ",num_messages_24h:desc";
  }

  // Build routing
  const routing = {
    router: history({
      parseURL({ qsModule, location }) {
        return parseInstantSearchUrlState(qsModule, location?.search ?? "");
      },
      push(url) {
        const urlObject = new URL(url);
        const params = new URLSearchParams(urlObject.search);
        //We exclude nsfw from routing
        params.delete("characters[toggle][is_nsfw]");
        const newUrl = urlObject.pathname + "?" + params.toString();
        navigate(newUrl);
      },
      getLocation() {
        return window.location;
      },
      cleanUrlOnDispose: false,
    }),
  };

  // Handle state changes
  const onSearchUiStateChange = ({ uiState, setUiState }: IStateChangeParams) => {
    let sortBy = null;
    if (uiState && Object.hasOwn(uiState, searchIndexName)) {
      const indexName = uiState[searchIndexName];

      if (indexName && Object.hasOwn(indexName, "sortBy")) {
        sortBy = indexName["sortBy"];
      }
    }
    setUserSortBy(sortBy || null);
    setUiState(uiState);
  };

  // Build filters
  const filters = [];

  if (applicationId) {
    filters.push(`application_ids:${applicationId}`);
  }
  if (charactersList.length > 0) {
    filters.push(`character_id:[${charactersList.map((id) => `'${id}'`).join(",")}]`);
  }
  if (username) {
    filters.push(`creator_username:${username}`);
  }
  const tagsToFilter = [];

  if (!isPartner) {
    tagsToFilter.push(TAGS_TO_EXCLUDE.STEP_FAMILY);
  }

  // filter NSFW tags in SFW mode
  if (shouldFilterNsfwCharacters) {
    tagsToFilter.push(...NSFW_TAGS);
  }

  if (tagsToFilter.length > 0 && collection === Collection.CHARACTERS) {
    filters.push(`tags:![${tagsToFilter.join(",")}]`);
  }

  if (shouldFilterNsfwCharacters) {
    filters.push(`is_nsfw:false`);
  }

  if (
    (!charactersList.length && config?.context === DisplayContext.FAVORITES) ||
    (!charactersList.length && dataSourceType === DataSourceType.FAVOURITES) ||
    stateIsFoundNegativeKeyword
  ) {
    filters.push(`character_id:null`);
  }

  /**
   * When user tries to navigate to some bot creator's page, we get username in props.
   * And blocked creator object is always present since it's stored globally in redux.
   * Thus to render creator page for a blocked creator, we need username check below.
   */
  if (blockedUsers?.length && !username) {
    filters.push(`creator_user_id:![${blockedUsers.map((id) => `'${id}'`).join(",")}]`);
  }

  // Exclude specific character IDs if provided in config (only for MEMBER_SELECT context)
  if (config?.context === DisplayContext.MEMBER_SELECT && "excludedCharacterIds" in config && config.excludedCharacterIds?.length) {
    filters.push(`character_id:![${config.excludedCharacterIds.map((id) => `'${id}'`).join(",")}]`);
  }

  // Filter out META (GROUP) characters when in MEMBER_SELECT context
  if (config?.context === DisplayContext.MEMBER_SELECT) {
    filters.push(`type:${CharacterType.STANDARD}`);
  }

  if (collection === Collection.CHARACTERS && config?.context === DisplayContext.MEMBER_SELECT) {
    filters.push(createGroupAddableMemberFilter({ onlyUserId }));
  }

  const filter = filters.join(" && ");

  const configProps = {
    hitsPerPage: 48,
    maxValuesPerFacet: 100,
    filters: filter,
  } as ConfigureProps;

  return (
    <InstantSearch searchClient={searchClient!} indexName={searchIndexName} routing={routing} onStateChange={onSearchUiStateChange}>
      <Configure {...configProps} />
      <Row className="flex-grow px-4 py-2.5 max-mob:p-2.5">
        <FilterBox
          collection={collection}
          useStoreTagFilters={useStoreTagFilters}
          attribute="tags"
          operator="and"
          isTagsDrawerVisible={stateIsTagsDrawerVisible}
          setIsTagsDrawerVisible={setStateIsTagsDrawerVisible}
          isTagListMinimized={stateIsTagListMinimized}
          context={config?.context}
        />
        <Col className={`gap-[18px]`}>
          {(() => {
            if (config?.headerComponent === HeaderComponentType.CUSTOM && config?.customHeader) {
              return config.customHeader;
            }
            if (config?.headerComponent === HeaderComponentType.NONE) {
              return null;
            }
            return (
              <>
                {!config?.hideBannedBanner && permissions?.includes("temporary_shadowban") ? <BannedBanner banType="shadow" /> : null}
                {config?.headerComponent !== HeaderComponentType.GROUP_CHAT && <HeroBanner />}
              </>
            );
          })()}

          <div className="flex gap-2.5 justify-between max-mob:flex-col">
            <Row className="gap-2.5 grow">
              <SearchTagFilterTrigger
                tagListMinimized={stateIsTagListMinimized}
                setTagListMinimized={setStateIsTagListMinimized}
                setTagsDrawer={setStateIsTagsDrawerVisible}
                onlyDesktop
              />
              <SharedChatbotsSearchBar
                queryBy={queryBy}
                setQueryBy={() => {}}
                defaultQueryBy=""
                setFoundNegativeKeyword={setStateIsFoundNegativeKeyword}
              />
            </Row>
            <Row className="gap-2.5">
              {!config?.hideNsfwToggle && isNsfwCharacterDiscoveryAllowed && isNsfwToggleAllowed ? (
                <NsfwToggle showNSFW={showNSFW} isNsfwToggled={nsfwToggle} setNsfwToggle={setNsfwToggle} />
              ) : null}
              <Row className="gap-2.5 max-mob:gap-[7px] max-mob:ml-auto">
                <SearchTagFilterTrigger
                  tagListMinimized={stateIsTagListMinimized}
                  setTagListMinimized={setStateIsTagListMinimized}
                  setTagsDrawer={setStateIsTagsDrawerVisible}
                  notDesktop
                />

                {/* Custom filters (e.g., MemberList) */}
                {customFilters}

                <SharedChatbotsSortDropdown
                  baseIndexName={baseIndexName}
                  currentRefinement={searchIndexName}
                  setCurrentCategory={setStateCurrentCategory}
                />
              </Row>
            </Row>
          </div>

          {!config?.hideWaitingQueue && <WaitingQueueHeader />}

          <Stats className="text-label-sm font-regular" translations={statsTranslations} />
          <CharacterHitsV2
            context={config?.context || DisplayContext.SHARED_CHATBOTS}
            nsfwToggle={isEffectiveNsfwToggleEnabled}
            recommId={recommId}
            isPartner={isPartner}
            currentCategory={stateCurrentCategory}
            onItemAction={onItemAction}
            selectedItems={selectedItems || []}
            maxItemsToSelect={maxItemsToSelect}
            onLimitReached={onLimitReached}
            shouldFilterNsfwCharacters={shouldFilterNsfwCharacters}
          />
        </Col>
      </Row>
      <Spacer y={6} />
      <Row justify="center">
        <SearchClientPagination />
      </Row>
    </InstantSearch>
  );
};

export default SharedChatbotsListing;
